import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LogIn, LogOut } from 'lucide-react';
import type { Paginated } from '@timesheet/shared';
import { apiOrThrow } from '@/lib/api';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';

interface ClockLogRow {
  id: string;
  ownerId: string;
  ownerUsername: string | null;
  ownerFullName: string | null;
  eventType: 'clock_in' | 'clock_out';
  serverTs: string;
  gpsLat: number;
  gpsLng: number;
  gpsAccuracy: number;
  cfCity: string | null;
  cfCountry: string | null;
  ipAddress: string | null;
  note: string | null;
}

function todayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

export function ClockLogPage() {
  const [fromDate, setFromDate] = useState(daysAgo(7));
  const [toDate, setToDate] = useState(todayDate());
  const [eventType, setEventType] = useState<'' | 'clock_in' | 'clock_out'>('');

  const query = useQuery({
    queryKey: ['clock', 'log', { fromDate, toDate, eventType }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      if (eventType) params.set('eventType', eventType);
      params.set('pageSize', '100');
      return apiOrThrow<Paginated<ClockLogRow>>(`/clock/log?${params.toString()}`);
    },
  });

  const columns: Column<ClockLogRow>[] = [
    {
      key: 'serverTs',
      header: 'Timestamp (server)',
      render: (r) => (
        <div className="font-mono text-xs">
          {new Date(r.serverTs).toLocaleString()}
        </div>
      ),
    },
    {
      key: 'user',
      header: 'User',
      render: (r) => (
        <div>
          <div className="font-medium">{r.ownerFullName ?? '—'}</div>
          <div className="text-xs text-brand-gray-mid">@{r.ownerUsername ?? '—'}</div>
        </div>
      ),
    },
    {
      key: 'eventType',
      header: 'Event',
      render: (r) =>
        r.eventType === 'clock_in' ? (
          <Badge tone="success">
            <LogIn className="mr-1 h-3 w-3" aria-hidden />
            IN
          </Badge>
        ) : (
          <Badge tone="warning">
            <LogOut className="mr-1 h-3 w-3" aria-hidden />
            OUT
          </Badge>
        ),
    },
    {
      key: 'gps',
      header: 'GPS',
      render: (r) => (
        <div className="font-mono text-xs">
          {r.gpsLat.toFixed(5)}, {r.gpsLng.toFixed(5)}
          <div className="text-brand-gray-mid">±{Math.round(r.gpsAccuracy)}m</div>
        </div>
      ),
    },
    {
      key: 'location',
      header: 'CF location',
      render: (r) => {
        const parts = [r.cfCity, r.cfCountry].filter(Boolean);
        return parts.length > 0 ? (
          parts.join(', ')
        ) : (
          <span className="text-brand-gray-mid">—</span>
        );
      },
    },
    {
      key: 'note',
      header: 'Note',
      render: (r) =>
        r.note ? (
          <span className="text-sm italic">"{r.note}"</span>
        ) : (
          <span className="text-brand-gray-mid">—</span>
        ),
      grow: true,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-brand-charcoal">Clock log</h1>
        <p className="mt-1 text-sm text-brand-gray-mid">
          Audit-grade event log. Server timestamp + GPS + Cloudflare geo + user agent.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-brand-gray-light bg-white p-3 shadow-card">
        <div>
          <label className="block text-xs font-medium text-brand-gray-mid">From</label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-brand-gray-mid">To</label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-40"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-brand-gray-mid">Event type</label>
          <Select
            value={eventType}
            onChange={(e) => setEventType(e.target.value as typeof eventType)}
            className="w-36"
          >
            <option value="">All</option>
            <option value="clock_in">Clock IN</option>
            <option value="clock_out">Clock OUT</option>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={query.data?.items}
        loading={query.isPending}
        total={query.data?.total}
        rowKey={(r) => r.id}
        emptyMessage="No clock events in this date range."
      />
    </div>
  );
}
